import type { MarketingAdCreative } from "@common/interfaces/KYDMarketing";
import {
  buildCreatorInstagramToFacebookPageIdMap,
  isApprovedBrandedContentPermissionStatus,
  normalizeMetaGraphId,
} from "@common/utils/metaPartnershipAdCreative";
import { facebookPageWithResolvedLinkedInstagram } from "./creativesWithInstagramFromFetch";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";

type PartnershipPermissionFields = Pick<
  FacebookPage,
  | "partnership_permission_status"
  | "partnership_status_label"
  | "partnership_has_permission_row"
  | "partnership_select_badge"
  | "partnership_select_badge_label"
  | "partnership_permission_url"
  | "partnership_permission_id"
>;

/** Higher score = fresher or more definitive partnership permission metadata. */
export function partnershipPermissionAuthorityScore(
  source: PartnershipPermissionFields | undefined
): number {
  if (!source) {
    return 0;
  }
  const status = source.partnership_permission_status;
  if (status && isApprovedBrandedContentPermissionStatus(status)) {
    return 4;
  }
  if (status === "PENDING" || status === "REVOKED") {
    return 3;
  }
  if (source.partnership_has_permission_row === true) {
    return 2;
  }
  if (status && status !== "UNKNOWN") {
    return 2;
  }
  return 1;
}

function pickPartnershipPermissionFields<T extends PartnershipPermissionFields>(
  embedded: T,
  fromList: T
): PartnershipPermissionFields {
  const embeddedScore = partnershipPermissionAuthorityScore(embedded);
  const listScore = partnershipPermissionAuthorityScore(fromList);
  const preferred = embeddedScore >= listScore ? embedded : fromList;
  return {
    partnership_permission_status: preferred.partnership_permission_status,
    partnership_status_label: preferred.partnership_status_label,
    partnership_has_permission_row: preferred.partnership_has_permission_row,
    partnership_select_badge: preferred.partnership_select_badge,
    partnership_select_badge_label: preferred.partnership_select_badge_label,
    partnership_permission_url: preferred.partnership_permission_url,
    partnership_permission_id: preferred.partnership_permission_id,
  };
}

function mergeCreativePartnerFacebookPageFromSources(
  embedded: FacebookPage,
  fromList: FacebookPage
): FacebookPage {
  return {
    ...embedded,
    ...fromList,
    ...pickPartnershipPermissionFields(embedded, fromList),
  };
}

function mergeCreativePartnerInstagramAccountFromSources(
  embedded: InstagramAccount,
  fromList: InstagramAccount
): InstagramAccount {
  return {
    ...embedded,
    ...fromList,
    ...pickPartnershipPermissionFields(embedded, fromList),
  };
}

export type MarketingPartnership = NonNullable<
  MarketingAdCreative["partnership"]
>;

export function formatAdPreviewUsername(username: string): string {
  return username.trim().replace(/^@+/, "");
}

/** Minimal IG row for selects when Meta linked account is not in partnership options list. */
export function stubPartnerInstagramAccount(args: {
  instagramUserId?: string;
  instagramUsername?: string;
  name?: string;
  profilePictureUrl?: string;
}): InstagramAccount | undefined {
  const id = args.instagramUserId?.trim();
  const username = args.instagramUsername
    ? formatAdPreviewUsername(args.instagramUsername)
    : "";
  if (!id && !username) {
    return undefined;
  }
  return {
    id: id || `username:${username}`,
    name: args.name?.trim() || username || "Instagram partner",
    username,
    followers_count: 0,
    profile_picture_url: args.profilePictureUrl?.trim() || "",
  };
}

/** Meta Graph user id (not a UI-only `username:` stub from {@link stubPartnerInstagramAccount}). */
export function isSyntheticPartnerInstagramUserId(
  instagramUserId: string | undefined | null
): boolean {
  const id = instagramUserId?.trim();
  return !id || id.startsWith("username:");
}

/** Resolve Meta ids for the API wire from form ids or embedded selection objects. */
export function resolvePartnerIdsForGuidedWire(creative: CreativeBlock): {
  partnerFacebookPageId?: string;
  partnerInstagramUserId?: string;
  partnerInstagramUsername?: string;
} {
  const rawPartnerInstagramUserId =
    creative.partnerInstagramUserId?.trim() ||
    creative.partnerInstagramAccount?.id?.trim() ||
    undefined;
  return {
    partnerFacebookPageId:
      creative.partnerFacebookPageId?.trim() ||
      creative.partnerFacebookPage?.id?.trim() ||
      undefined,
    partnerInstagramUserId: isSyntheticPartnerInstagramUserId(
      rawPartnerInstagramUserId
    )
      ? undefined
      : rawPartnerInstagramUserId,
    partnerInstagramUsername:
      creative.partnerInstagramUsername?.trim() ||
      creative.partnerInstagramAccount?.username?.trim() ||
      undefined,
  };
}

export function resolvePartnerFacebookPageById(
  pageId: string | undefined | null,
  pages: readonly FacebookPage[]
): FacebookPage | undefined {
  const id = pageId?.trim();
  return id ? pages.find((page) => page.id === id) : undefined;
}

export type PartnerInstagramFieldsFromLinkedPage = Pick<
  CreativeBlock,
  "partnerInstagramUserId" | "partnerInstagramUsername" | "partnerInstagramAccount"
>;

/** Partner IG fields implied by a Facebook page's linked Instagram in Meta. */
export function facebookPageHasLinkedInstagram(page: FacebookPage): boolean {
  return Boolean(
    page.linked_instagram_user_id?.trim() ||
      page.linked_instagram_username?.trim()
  );
}

/** False when the page has a linked IG in Meta and the selected partner IG is a different account. */
export function isPartnerFacebookPageCompatibleWithInstagram(
  page: FacebookPage,
  partnerInstagramUserId: string | undefined,
  partnerInstagramUsername: string | undefined,
  instagramAccounts: readonly InstagramAccount[]
): boolean {
  const linkedFields = partnerInstagramFieldsFromLinkedFacebookPage(
    page,
    instagramAccounts
  );
  if (!linkedFields) {
    return true;
  }
  const partnerId = partnerInstagramUserId?.trim();
  const partnerUsername = partnerInstagramUsername?.trim()
    ? formatAdPreviewUsername(partnerInstagramUsername)
    : "";
  if (!partnerId && !partnerUsername) {
    return true;
  }
  const linkedId = linkedFields.partnerInstagramUserId?.trim();
  const linkedUsername = linkedFields.partnerInstagramUsername?.trim()
    ? formatAdPreviewUsername(linkedFields.partnerInstagramUsername)
    : "";
  if (linkedId && partnerId) {
    return partnerId === linkedId;
  }
  if (linkedUsername && partnerUsername) {
    return linkedUsername === partnerUsername;
  }
  return false;
}

export function isPartnerInstagramAccountCompatibleWithFacebookPage(
  account: InstagramAccount,
  page: FacebookPage | undefined,
  instagramAccounts: readonly InstagramAccount[]
): boolean {
  if (!page) {
    return true;
  }
  return isPartnerFacebookPageCompatibleWithInstagram(
    page,
    account.id,
    account.username,
    instagramAccounts
  );
}

export function partnerInstagramFieldsAfterFacebookPageSelected(
  page: FacebookPage,
  instagramAccounts: readonly InstagramAccount[],
  fetchedAccounts?: readonly InstagramAccount[],
  options?: { inferFromFirstFetchedWhenUndeclared?: boolean }
): PartnerInstagramFieldsFromLinkedPage | null {
  const resolvedPage = fetchedAccounts?.length
    ? facebookPageWithResolvedLinkedInstagram(page, fetchedAccounts, {
        inferFromFirstFetchedWhenUndeclared:
          options?.inferFromFirstFetchedWhenUndeclared ?? true,
      })
    : page;
  return partnerInstagramFieldsFromLinkedFacebookPage(
    resolvedPage,
    instagramAccounts
  );
}

export function partnerInstagramFieldsFromLinkedFacebookPage(
  page: FacebookPage,
  instagramAccounts: readonly InstagramAccount[]
): PartnerInstagramFieldsFromLinkedPage | null {
  const linkedId = page.linked_instagram_user_id?.trim();
  const linkedUsername = page.linked_instagram_username
    ? formatAdPreviewUsername(page.linked_instagram_username)
    : "";
  if (!linkedId && !linkedUsername) {
    return null;
  }

  const fromList = resolvePartnerInstagramAccountByWire(
    linkedId,
    linkedUsername,
    instagramAccounts
  );
  if (fromList) {
    return {
      partnerInstagramUserId: fromList.id,
      partnerInstagramUsername: fromList.username?.trim() || undefined,
      partnerInstagramAccount: fromList,
    };
  }

  const stub = stubPartnerInstagramAccount({
    instagramUserId: linkedId,
    instagramUsername: linkedUsername,
  });
  if (!stub) {
    return null;
  }

  return {
    partnerInstagramUserId: linkedId || stub.id,
    partnerInstagramUsername: linkedUsername || stub.username || undefined,
    partnerInstagramAccount: stub,
  };
}

/** Facebook page linked to a partner Instagram account in Meta (page catalog). */
export function resolvePartnerFacebookPageLinkedToInstagram(
  account: InstagramAccount | undefined | null,
  pages: readonly FacebookPage[]
): FacebookPage | undefined {
  const igId = account?.id?.trim();
  const igUsername = account?.username?.trim()
    ? formatAdPreviewUsername(account.username)
    : "";
  if (!igId && !igUsername) {
    return undefined;
  }

  if (igId) {
    const map = buildCreatorInstagramToFacebookPageIdMap(
      pages.map((page) => ({
        id: page.id,
        linkedInstagramUserId: page.linked_instagram_user_id,
      }))
    );
    const normalizedIgId = normalizeMetaGraphId(igId) ?? igId;
    const pageId = map.get(normalizedIgId);
    if (pageId) {
      return resolvePartnerFacebookPageById(pageId, pages);
    }
  }

  if (igUsername) {
    return pages.find((page) => {
      const linkedUsername = page.linked_instagram_username?.trim();
      return (
        Boolean(linkedUsername) &&
        formatAdPreviewUsername(linkedUsername!) === igUsername
      );
    });
  }

  return undefined;
}

export function partnerFacebookFieldsFromLinkedInstagramAccount(
  account: InstagramAccount,
  pages: readonly FacebookPage[]
): Pick<CreativeBlock, "partnerFacebookPageId" | "partnerFacebookPage"> | null {
  const page = resolvePartnerFacebookPageLinkedToInstagram(account, pages);
  if (!page) {
    return null;
  }
  return {
    partnerFacebookPageId: page.id,
    partnerFacebookPage: page,
  };
}

/** Keep an explicit partner Facebook pick when IG reverse-lookup misses catalog metadata. */
export function partnerFacebookFieldsAfterInstagramSelected(
  account: InstagramAccount,
  pages: readonly FacebookPage[],
  instagramAccounts: readonly InstagramAccount[],
  existingPartnerPage?: FacebookPage
): Pick<CreativeBlock, "partnerFacebookPageId" | "partnerFacebookPage"> | null {
  const linkedPageFields = partnerFacebookFieldsFromLinkedInstagramAccount(
    account,
    pages
  );
  if (linkedPageFields) {
    return linkedPageFields;
  }
  if (
    existingPartnerPage &&
    isPartnerInstagramAccountCompatibleWithFacebookPage(
      account,
      existingPartnerPage,
      instagramAccounts
    )
  ) {
    return {
      partnerFacebookPageId: existingPartnerPage.id,
      partnerFacebookPage: existingPartnerPage,
    };
  }
  return null;
}

/** True when the stored partner page is the IG account's linked page in Meta (not picked via FB select). */
export function isPartnerFacebookPageInferredFromInstagramSelection(args: {
  partnerInstagramUserId?: string;
  partnerInstagramUsername?: string;
  partnerFacebookPageId?: string;
  pages: readonly FacebookPage[];
  instagramAccounts: readonly InstagramAccount[];
}): boolean {
  const partnerFacebookPageId = args.partnerFacebookPageId?.trim();
  if (!partnerFacebookPageId) {
    return false;
  }
  const account =
    resolvePartnerInstagramAccountByWire(
      args.partnerInstagramUserId,
      args.partnerInstagramUsername,
      args.instagramAccounts
    ) ??
    stubPartnerInstagramAccount({
      instagramUserId: args.partnerInstagramUserId,
      instagramUsername: args.partnerInstagramUsername,
    });
  if (!account) {
    return false;
  }
  const linkedPage = resolvePartnerFacebookPageLinkedToInstagram(
    account,
    args.pages
  );
  return linkedPage?.id === partnerFacebookPageId;
}

export function resolvePartnerInstagramAccountByWire(
  partnerInstagramUserId: string | undefined | null,
  partnerInstagramUsername: string | undefined | null,
  instagramAccounts: readonly InstagramAccount[]
): InstagramAccount | undefined {
  const userId = partnerInstagramUserId?.trim();
  if (userId) {
    const normalizedUserId = normalizeMetaGraphId(userId);
    return instagramAccounts.find(
      (account) => normalizeMetaGraphId(account.id) === normalizedUserId
    );
  }
  const username = partnerInstagramUsername?.trim();
  if (!username) {
    return undefined;
  }
  const normalized = formatAdPreviewUsername(username);
  return instagramAccounts.find(
    (account) => formatAdPreviewUsername(account.username) === normalized
  );
}

export function resolveCreativeBlockPartnerPlatforms(creative: CreativeBlock): {
  instagram: boolean;
  facebook: boolean;
} {
  const ids = resolvePartnerIdsForGuidedWire(creative);
  return {
    instagram: Boolean(
      ids.partnerInstagramUserId || ids.partnerInstagramUsername
    ),
    facebook: Boolean(ids.partnerFacebookPageId),
  };
}

export function resolveMarketingPartnershipPlatforms(
  partnership: MarketingPartnership | undefined
): { instagram: boolean; facebook: boolean } {
  if (!partnership) {
    return { instagram: false, facebook: false };
  }
  return {
    instagram: Boolean(
      partnership.partner_instagram_user_id?.trim() ||
        partnership.partner_instagram_username?.trim()
    ),
    facebook: Boolean(partnership.partner_facebook_page_id?.trim()),
  };
}

export function resolveCreativePartnerFacebookPage(
  creative: CreativeBlock,
  pages: readonly FacebookPage[]
): FacebookPage | undefined {
  const pageId = resolvePartnerIdsForGuidedWire(creative).partnerFacebookPageId;
  const fromList = pageId
    ? resolvePartnerFacebookPageById(pageId, pages)
    : undefined;
  const embeddedName = creative.partnerFacebookPage?.name?.trim();
  const embedded =
    embeddedName && creative.partnerFacebookPage
      ? creative.partnerFacebookPage
      : undefined;

  if (fromList && embedded) {
    return mergeCreativePartnerFacebookPageFromSources(embedded, fromList);
  }
  return fromList ?? embedded;
}

export function resolveCreativePartnerInstagramAccount(
  creative: CreativeBlock,
  instagramAccounts: readonly InstagramAccount[]
): InstagramAccount | undefined {
  const ids = resolvePartnerIdsForGuidedWire(creative);
  const fromList = resolvePartnerInstagramAccountByWire(
    ids.partnerInstagramUserId,
    ids.partnerInstagramUsername,
    instagramAccounts
  );
  const embedded =
    creative.partnerInstagramAccount?.id?.trim() ||
    creative.partnerInstagramAccount?.username?.trim()
      ? creative.partnerInstagramAccount
      : undefined;

  if (fromList && embedded) {
    return mergeCreativePartnerInstagramAccountFromSources(embedded, fromList);
  }
  if (fromList) {
    return fromList;
  }
  if (embedded) {
    return embedded;
  }

  const page = creative.partnerFacebookPage;
  return stubPartnerInstagramAccount({
    instagramUserId:
      ids.partnerInstagramUserId ?? page?.linked_instagram_user_id,
    instagramUsername:
      ids.partnerInstagramUsername ?? page?.linked_instagram_username,
  });
}

export function resolveCreativePartnerFacebookName(
  creative: CreativeBlock,
  pages: readonly FacebookPage[]
): string | null {
  return resolveCreativePartnerFacebookPage(creative, pages)?.name?.trim() ?? null;
}

export function resolveCreativePartnerInstagramUsername(
  creative: CreativeBlock,
  instagramAccounts: readonly InstagramAccount[]
): string | null {
  const fromAccount = resolveCreativePartnerInstagramAccount(
    creative,
    instagramAccounts
  )?.username?.trim();
  if (fromAccount) {
    return formatAdPreviewUsername(fromAccount);
  }
  const username = resolvePartnerIdsForGuidedWire(creative)
    .partnerInstagramUsername;
  return username ? formatAdPreviewUsername(username) : null;
}

export function resolveMarketingPartnerFacebookName(
  partnership: MarketingPartnership | undefined,
  pages: readonly FacebookPage[]
): string | null {
  return (
    resolvePartnerFacebookPageById(
      partnership?.partner_facebook_page_id,
      pages
    )?.name?.trim() ?? null
  );
}

export function resolveMarketingPartnerFacebookDisplayLabel(
  partnership: MarketingPartnership | undefined,
  pages: readonly FacebookPage[]
): string | null {
  const name = resolveMarketingPartnerFacebookName(partnership, pages);
  if (name) {
    return name;
  }
  return partnership?.partner_facebook_page_id?.trim()
    ? "Facebook partner"
    : null;
}

export function resolveMarketingPartnerInstagramUsername(
  partnership: MarketingPartnership | undefined,
  instagramAccounts: readonly InstagramAccount[]
): string | null {
  const fromWire = partnership?.partner_instagram_username?.trim();
  if (fromWire) {
    return formatAdPreviewUsername(fromWire);
  }
  const account = resolvePartnerInstagramAccountByWire(
    partnership?.partner_instagram_user_id,
    undefined,
    instagramAccounts
  );
  return account?.username
    ? formatAdPreviewUsername(account.username)
    : null;
}
